import { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api } from '../../lib/api';
import { useAuth } from '../auth/use-auth';
import { TAGESENDE, type Faelligkeit } from '../../lib/faelligkeit';

/**
 * Die Felder einer Aufgabe — überall gleich aufgebaut (T-0057).
 *
 * Vorbild ist „Anfrage ausblenden“ aus T-0022: ein Datum, die Uhrzeit nur auf
 * Wunsch, sonst zum Ende des Tages. Die Aufgabenseite, die Aufgaben an einem
 * Historieneintrag und die Absage einer Probestunde hatten jeweils ein
 * ``datetime-local``-Feld, das mit dem Zeitpunkt des Öffnens vorbelegt war.
 */
export function FaelligkeitFeld({
  id,
  value,
  onChange,
}: {
  id: string;
  value: Faelligkeit;
  onChange: (next: Faelligkeit) => void;
}) {
  const mitUhrzeit = value.uhrzeit !== null;
  return (
    <div className="grid gap-2">
      <Label htmlFor={`${id}-datum`}>Fällig am</Label>
      <div className="flex flex-wrap items-center gap-3">
        <Input
          id={`${id}-datum`}
          type="date"
          className="w-auto"
          value={value.datum}
          onChange={(e) => onChange({ ...value, datum: e.target.value })}
          required
        />
        <div className="flex items-center gap-2">
          <Switch
            id={`${id}-uhrzeit-an`}
            checked={mitUhrzeit}
            onCheckedChange={(an) => onChange({ ...value, uhrzeit: an ? TAGESENDE : null })}
          />
          <Label htmlFor={`${id}-uhrzeit-an`} className="font-normal">Uhrzeit festlegen</Label>
        </div>
        {mitUhrzeit ? (
          <Input
            type="time"
            className="w-auto"
            aria-label="Uhrzeit"
            value={value.uhrzeit ?? TAGESENDE}
            onChange={(e) => onChange({ ...value, uhrzeit: e.target.value || TAGESENDE })}
          />
        ) : (
          <span className="text-xs text-muted-foreground">zum Ende des Tages</span>
        )}
      </div>
    </div>
  );
}

export type ZuweisbareBenutzer = Array<{ id: number; name: string }>;

export function ZuweisungFeld({
  id,
  value,
  users,
  onChange,
}: {
  id: string;
  value: number | undefined;
  users: ZuweisbareBenutzer;
  onChange: (id: number) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>Zugewiesen an</Label>
      {/* Immer ein String: undefined ließ die Auswahl von unkontrolliert zu
          kontrolliert wechseln — die Konsolenwarnung aus T-0057. */}
      <Select value={value ? String(value) : ''} onValueChange={(v) => onChange(Number.parseInt(v, 10))}>
        <SelectTrigger id={id}><SelectValue placeholder="Benutzer wählen" /></SelectTrigger>
        <SelectContent>
          {users.map((u) => (
            <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Wem man eine Aufgabe zuweisen kann; ohne Liste wenigstens sich selbst. */
export function useZuweisbareBenutzer(aktiv = true): ZuweisbareBenutzer {
  const { user } = useAuth();
  const [users, setUsers] = useState<ZuweisbareBenutzer>([]);
  useEffect(() => {
    if (!aktiv) return;
    let aktuell = true;
    const selbst = user ? Number.parseInt(String(user.id), 10) : 0;
    api
      .get<Array<{ id: number; vorname: string; nachname: string }>>('/api/users')
      .then((liste) => {
        if (aktuell) setUsers(liste.map((u) => ({ id: u.id, name: `${u.vorname} ${u.nachname}`.trim() })));
      })
      .catch(() => {
        if (aktuell && selbst) setUsers([{ id: selbst, name: user?.name || `User #${selbst}` }]);
      });
    return () => {
      aktuell = false;
    };
  }, [aktiv, user]);
  return users;
}
